import jsPDF from 'jspdf';
import 'jspdf-autotable';
import formatPrice from 'utils/formatPrice';
import patientDobToAge from 'utils/patientDobToAge';
import formatUtcToLocalDate from 'utils/formatUtcToLocalDate';

import dateFormat from 'config/dateFormat';
import timeDateFormat from 'config/timeDateFormat';

const patientNotesToPDF = ({ inputPDF, patient, notes, business }) => new Promise((resolve) => {
  const lineHeight = 1.2;
  const margin = 10;
  const ptsPerMm = 2.834646;
  const fontSize = 10;
  const titleFontSize = 18;
  const fontType = 'normal';
  const textColor = 0;
  const drawColor = 0;
  const lineHeightMm = fontSize * (lineHeight / ptsPerMm);
  const format = 'a4';
  // eslint-disable-next-line new-cap
  const doc = inputPDF || new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format,
    lineHeight,
  });
  if (inputPDF) {
    doc.addPage(format);
  }
  doc.setFont('times');
  let y = margin;
  let x = margin;
  const pageContentWidth = doc.internal.pageSize.getWidth() - (margin * 2);
  let maxWidth = pageContentWidth;
  const setDefaults = () => {
    doc.setFontSize(fontSize);
    doc.setFontType(fontType);
    doc.setTextColor(textColor);
    doc.setDrawColor(drawColor);
  };
  const setX = () => {
    x = margin;
  };
  const addY = (newY) => {
    if ((y + newY) < doc.internal.pageSize.getHeight() - (margin * 2)) {
      y += newY;
    } else {
      doc.addPage();
      y = margin;
    }
  };
  const renderTitle = () => {
    doc.setFontSize(titleFontSize);
    doc.setFontType('bold');
    x = (doc.internal.pageSize.getWidth() / 2);
    maxWidth = pageContentWidth * 0.75;
    const text = doc.splitTextToSize(`${patient['Last Name']}, ${patient['First Name']} Notes`, maxWidth);
    text.forEach((line) => {
      addY(titleFontSize * (lineHeight / ptsPerMm));
      doc.text(line, x, y, null, null, 'center');
    });
  };
  const renderPatientGeneral = () => {
    setDefaults();
    setX();
    maxWidth = (pageContentWidth / 2) - (margin / 2);
    const columns = [
      { title: 'Label', dataKey: 'label' },
      { title: 'Value', dataKey: 'value' },
    ];
    const rows = [
      ...[{
        label: 'Patient Name',
        value: `${patient['First Name']} ${patient['Middle Name'] || ''}, ${patient['Last Name']}`,
      },
      {
        label: patient['Date of Death']
          ? 'Date of Birth'
          : 'Date of Birth, Age',
        value: patient['Date of Death']
          ? patient['Date of Birth']
          : `${patient['Date of Birth']}, ${patientDobToAge(patient['Date of Birth'])}`,
      }],
      ...patient['Date of Death'] ? [{
        label: 'Date of Death',
        value: patient['Date of Death'],
      }] : [],
      ...[{
        label: 'Gender',
        value: patient.Gender,
      },
      {
        label: 'Social Security Number',
        value: patient['Social Security Number'] || '',
      },
      {
        label: 'Status',
        value: patient['Patient Status'] === true ? 'Active' : 'Inactive',
      }],
    ];
    const startY = y;
    addY((margin / 2) + lineHeightMm);
    doc.text('Personal Information', x, y);
    addY(margin / 4);
    doc.autoTable(columns, rows, {
      theme: 'grid',
      startY: y,
      showHeader: 'never',
      margin: [0, 0, 0, x],
      tableWidth: maxWidth,
      styles: {
        font: 'times',
        textColor,
        lineColor: drawColor,
        fontSize,
        cellPadding: [0, margin / 4, 0, margin / 4],
        columnWidth: 'auto',
        overflow: 'linebreak',
      },
      columnStyles: {
        label: {
          fontStyle: 'bold',
        },
      },
    });
    y = startY;
  };
  const renderPatientContactInformation = () => {
    setDefaults();
    x = (margin * 1.5) + (pageContentWidth / 2);
    maxWidth = (pageContentWidth / 2) - (margin / 2);
    const columns = [
      { title: 'Label', dataKey: 'label' },
      { title: 'Value', dataKey: 'value' },
    ];
    const addressInfo = patient['Address Information'];
    let address;
    if (addressInfo.Street) {
      address = addressInfo.Apt
        ? `${addressInfo.Street} ST. APT. ${addressInfo.Apt}, ${addressInfo.City}, ${addressInfo.State || ''} ${addressInfo['Zip Code']}`
        : `${addressInfo.Street} ST., ${addressInfo.City}, ${addressInfo.State || ''} ${addressInfo['Zip Code']}`;
    }
    const rows = [
      {
        label: 'Cell Phone',
        value: patient['Contact Information']['Primary Phone'] || '',
      },
      {
        label: 'Home Phone',
        value: patient['Contact Information']['Home Phone'] || '',
      },
      {
        label: 'Emergency Contact',
        value: patient['Contact Information']['Work Phone'] || '',
      },
      {
        label: 'Email',
        value: patient['Contact Information'].Email || '',
      },
      {
        label: 'Prefered Method of communication',
        value: patient['Contact Information']['Preferred method of communications'] || '',
      },
      {
        label: 'Address',
        value: address,
      },
    ];
    addY((margin / 2) + lineHeightMm);
    doc.text('Contact Information', x, y);
    addY(margin / 4);
    doc.autoTable(columns, rows, {
      theme: 'grid',
      startY: y,
      showHeader: 'never',
      margin: [0, 0, 0, x],
      tableWidth: maxWidth,
      styles: {
        font: 'times',
        textColor,
        lineColor: drawColor,
        fontSize,
        cellPadding: [0, margin / 4, 0, margin / 4],
        columnWidth: 'auto',
        overflow: 'linebreak',
      },
      columnStyles: {
        label: {
          fontStyle: 'bold',
        },
      },
    });
    const table = doc.autoTable.previous;
    addY(table.finalY - y);
  };
  const renderPatientInsurance = () => {
    const insurances = patient['Insurance Information'].filter(item =>
      item['Insurance Type'] !== 'Self');
    setDefaults();
    setX();
    maxWidth = pageContentWidth;
    addY((margin / 2) + lineHeightMm);
    if (insurances.length > 0) {
      const columns = [
        { title: 'Payer', dataKey: 'payerName' },
        { title: 'Order of\n Benefits', dataKey: 'orderBenefits' },
        { title: 'Type', dataKey: 'type' },
        { title: 'Insurance ID', dataKey: 'insuranceID' },
        { title: 'Effective From/To', dataKey: 'effectiveFromTo' },
        { title: 'Copay', dataKey: 'copay' },
        { title: 'Status', dataKey: 'status' },
      ];
      const rows = insurances.map((item) => {
        const effectiveDates = item['Effective From'] && item['Effective To']
          ? `${item['Effective From']} - ${item['Effective To']}` : '';
        let copay;
        switch (item['Copay Type']) {
          case 'Fixed':
            copay = formatPrice(item['Copay Amount']);
            break;
          case 'Percent':
            copay = `${item['Copay Amount']}%`;
            break;
          default:
            copay = '';
        }
        return {
          payerName: item['Insurance Info']['Payer Name'],
          orderBenefits: item['Order Benefits'],
          type: item['Claim Filling Code'] && item['Claim Filling Code']['Full Name']
            ? item['Claim Filling Code']['Full Name'] : '',
          insuranceID: item['Insurance ID'],
          effectiveFromTo: effectiveDates,
          copay,
          status: item['Insurance Status'],
        };
      });
      doc.text('Insurance Information', x, y);
      addY(margin / 4);
      doc.autoTable(columns, rows, {
        theme: 'grid',
        startY: y,
        showHeader: 'everyPage',
        margin: [0, 0, 0, x],
        tableWidth: maxWidth,
        styles: {
          font: 'times',
          textColor,
          lineColor: drawColor,
          fontSize,
          cellPadding: [0, margin / 4, 0, margin / 4],
          columnWidth: 'auto',
          overflow: 'linebreak',
        },
        headerStyles: {
          lineWidth: 0.1,
          lineColor: drawColor,
          fillColor: 'white',
        },
        columnStyles: {
          orderBenefits: {
            columnWidth: 'wrap',
          },
          type: {
            columnWidth: 'wrap',
          },
          insuranceID: {
            columnWidth: 27,
          },
          effectiveFromTo: {
            columnWidth: 24,
          },
          copay: {
            columnWidth: 'wrap',
          },
          status: {
            columnWidth: 'wrap',
          },
        },
      });
      const table = doc.autoTable.previous;
      addY(table.finalY - y);
    } else {
      doc.text('Patient has no Insurance', x, y);
    }
  };
  const renderPatientNotes = () => {
    setDefaults();
    setX();
    maxWidth = pageContentWidth;
    addY((margin / 2) + lineHeightMm);
    if (notes.length > 0) {
      const columns = [
        { title: 'Note', dataKey: 'note' },
        { title: 'Category', dataKey: 'category' },
        { title: 'Author', dataKey: 'author' },
        { title: 'Date', dataKey: 'date' },     
      ];
      const rows = notes.map(item => ({
        note: item.Text,
        category: item['Notes Category'].map(categoryItem => categoryItem.Name).join(', '),
        author: `${item.Author['First Name']} ${item.Author['Last Name']}`,
        date: formatUtcToLocalDate(
          item['Create At'],
          timeDateFormat,
          dateFormat,
          business['Company Address']['Time Zone'],
          business['Company Address']['Observes Daylight Saving']),
        
      }));
      doc.text('Notes', x, y);
      addY(margin / 4);
      doc.autoTable(columns, rows, {
        theme: 'grid',
        startY: y,
        showHeader: 'everyPage',
        margin: [5, 0, 5, x],
        tableWidth: maxWidth,
        styles: {
          font: 'times',
          textColor,
          lineColor: drawColor,
          fontSize,
          cellPadding: [0, margin / 4, 0, margin / 4],
          columnWidth: 'auto',
          overflow: 'linebreak',
        },
        headerStyles: {
          lineWidth: 0.1,
          lineColor: drawColor,
          fillColor: 'white',
        },
        columnStyles: {
          category: {
            columnWidth: 30,
          },
          author: {
            columnWidth: 30,
          },
          date: {
            columnWidth: 'wrap',
          },
        },
      });
      const table = doc.autoTable.previous;
      addY(table.finalY - y);
    } else {
      doc.text('Patient has no notes', x, y);
    }
  };

  renderTitle();
  renderPatientGeneral();
  renderPatientContactInformation();
  renderPatientInsurance();
  renderPatientNotes();
  resolve(doc);
});

export default patientNotesToPDF;



// WEBPACK FOOTER //
// ./src/utils/patientNotesToPDF.js